/**
 * Global Navbar Plugin for GrapesJS
 * Auto-injects a navigation bar on every page. Users edit it via NavigationSettingsModal.
 */

import type { GrapesJSEditor } from '../types/grapesjs';
import { slugify } from '../utils/slugify';

export interface NavLink {
  label: string;
  href: string;
}

const NAV_STORAGE_KEY = 'gdx-nav-config';

// No nav config has ever been explicitly saved for this project -- build
// default links from the real pages that exist right now instead of a
// hardcoded /listings /about /contact guess that's disconnected from
// whatever pages actually get published. The app's own "+Add" control names
// new pages "Page 2"/"Page 3"/etc, and MainNav's handlePublish derives each
// page's real URL by slugifying that exact name -- using the same slugify
// here means a never-renamed page still gets a nav link that resolves,
// instead of 404ing by default. See issue #36.
function buildDefaultNavFromPages(editor?: any): NavLink[] {
  try {
    const pages = editor?.Pages?.getAll?.() || [];
    if (!pages.length) return [{ label: 'Home', href: '/' }];
    const mainPage = editor?.Pages?.getMain?.() || pages[0];
    return pages.map((page: any) => {
      const isMain = mainPage && page.getId() === mainPage.getId();
      // Slug source must match MainNav's handlePublish exactly (name, falling
      // back to the page id) -- any divergence here reintroduces the
      // link/published-URL mismatch this fix exists to close.
      const slugSource = page.getName?.() || page.getId();
      return {
        label: isMain ? 'Home' : (page.getName?.() || 'Untitled'),
        href: isMain ? '/' : `/${slugify(slugSource)}`,
      };
    });
  } catch {
    return [{ label: 'Home', href: '/' }];
  }
}

export function getNavConfig(editor?: any): NavLink[] {
  try {
    const stored = localStorage.getItem(NAV_STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return buildDefaultNavFromPages(editor);
}

export function saveNavConfig(links: NavLink[]): void {
  localStorage.setItem(NAV_STORAGE_KEY, JSON.stringify(links));
}

// Float-based layout — avoids display:flex which GrapesJS presets can interfere with.
// <a> tags directly in a float:right div means no ul/li default styles to fight.
export function buildNavHtml(links: NavLink[]): string {
  const linksHtml = links
    .map(l =>
      `<a href="${l.href}" style="display:inline-block;color:#374151;text-decoration:none;font-size:14px;font-weight:500;padding:8px 14px;border-radius:6px;vertical-align:middle;">${l.label}</a>`
    )
    .join('');
  return (
    `<nav data-global-component="true" style="background:#ffffff;border-bottom:2px solid #e2e8f0;width:100%;display:block;box-sizing:border-box;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;">` +
    `<div style="max-width:1200px;margin:0 auto;padding:0 24px;overflow:hidden;box-sizing:border-box;min-height:60px;">` +
    `<span style="display:inline-block;vertical-align:middle;font-size:22px;font-weight:700;color:#007AFF;letter-spacing:-0.5px;line-height:60px;">Logo</span>` +
    `<div style="float:right;line-height:60px;">${linksHtml}</div>` +
    `</div></nav>`
  );
}

// Injects a <style> tag directly into the canvas iframe <head> so our rules win
// regardless of what grapesjs-preset-webpage adds to the canvas stylesheet.
function injectNavCanvasStyles(editor: any): void {
  try {
    const canvasDoc = editor.Canvas?.getDocument?.();
    if (!canvasDoc || !canvasDoc.head) return;
    if (canvasDoc.getElementById('gdx-nav-styles')) return;
    const style = canvasDoc.createElement('style');
    style.id = 'gdx-nav-styles';
    style.textContent =
      'nav[data-global-component="true"]{display:block!important;background:#ffffff!important;border-bottom:2px solid #e2e8f0!important;width:100%!important;box-sizing:border-box!important;}' +
      'nav[data-global-component="true"] a{color:#374151!important;text-decoration:none!important;display:inline-block!important;font-size:14px!important;font-weight:500!important;}' +
      'nav[data-global-component="true"] span{color:#007AFF!important;}';
    canvasDoc.head.appendChild(style);
  } catch (e) {
    console.warn('🌐 Could not inject nav canvas styles:', e);
  }
}

// Component.prototype.find() is DOM/view-backed -- GrapesJS's own doc
// comment on it: "ATTENTION: this method works only with already rendered
// component". A page that isn't the one currently open in the canvas has
// no live view, so find() silently returns [] for it even when its model
// tree genuinely has a navbar -- this was previously causing updates to
// only ever reach whichever page happened to be selected at the moment
// (see issue #36: the 2nd+ page added after the 1st was selected would
// resync every page's nav *except* the one no longer in view). Querying
// root.components() directly instead -- a plain model-tree traversal with
// no rendering dependency -- works for every page regardless of which one
// is currently open.
function findNavbarComponents(root: any): any[] {
  try {
    const children = root?.components?.();
    if (!children || typeof children.filter !== 'function') return [];
    return children.filter((comp: any) => comp?.getAttributes?.()?.['data-global-component'] === 'true');
  } catch {
    return [];
  }
}

// Whether a given page's real component tree already has a navbar. Reads
// the page's own root component directly (page.getMainComponent()), not
// editor.getHtml()/editor.getSelected() — those act on whichever page is
// currently selected in the canvas, which is not necessarily the page this
// function is being asked about (e.g. editor.Pages.add() does not select
// the page it just created).
function pageHasNavbar(page: any): boolean {
  try {
    const root = page?.getMainComponent?.();
    if (!root) return false;
    return findNavbarComponents(root).length > 0;
  } catch {
    return false;
  }
}

function isEmptyPage(page: any): boolean {
  try {
    const root = page?.getMainComponent?.();
    const children = root?.components?.();
    return !children || children.length === 0;
  } catch {
    return true;
  }
}

// Bakes the navbar into a page's actual component tree once, at creation
// time. This is a synchronous, model-only operation (root.components().add)
// — unlike editor.setComponents(), it does not require the canvas to have
// mounted a view for this page yet, so there's no frame-load race to gate
// on. Only ever called for pages confirmed empty by the caller — this never
// force-adds a navbar back onto a page that already has real content,
// including one where an agent deliberately removed it.
function injectNavbarIntoPage(editor: any, page: any): void {
  try {
    const root = page?.getMainComponent?.();
    if (!root || pageHasNavbar(page)) return;
    console.log('🌐 Baking global navbar into page:', page.getName?.() || page.getId?.());
    root.components().add(buildNavHtml(getNavConfig(editor)), { at: 0 });
  } catch (e) {
    console.warn('🌐 Could not bake in navbar:', e);
  }
}

// Replaces whatever navbar a specific page's own component tree already
// has (if any) with a freshly-built one. Operates on the page's model
// directly (root.components()), the same approach injectNavbarIntoPage
// above already uses — unlike editor.getHtml()/setComponents(), this
// doesn't depend on which page happens to be selected in the canvas.
function replaceNavbarInPage(page: any, navHtml: string): void {
  try {
    const root = page?.getMainComponent?.();
    if (!root) return;
    findNavbarComponents(root).forEach((comp: any) => comp.remove());
    root.components().add(navHtml, { at: 0 });
  } catch (e) {
    console.warn('🌐 Could not replace navbar on page:', page?.getName?.() || page?.getId?.(), e);
  }
}

// Called from NavigationSettingsModal after saving — rebuilds the nav with
// updated links on every page, not just whichever one is open in the
// canvas right now. Publishing exports every page in one pass (MainNav's
// handlePublish), so a nav update that only reached the active page would
// leave every other page shipping with stale links. See issue #36.
export function updateNavbarInEditor(editor: any): void {
  try {
    const navHtml = buildNavHtml(getNavConfig(editor));
    const pages = editor.Pages?.getAll?.() || [];
    pages.forEach((page: any) => replaceNavbarInPage(page, navHtml));
    injectNavCanvasStyles(editor);
    console.log(`✅ Global navbar updated across ${pages.length} page(s)`);
  } catch (e) {
    console.warn('🌐 Could not update navbar:', e);
  }
}

// Called whenever a page is added via "+Add". Rebuilds the navbar on every
// page that currently has one (so an existing page's links pick up the
// newly added page too, not just the new page itself) using the live page
// list, while leaving alone any page where a navbar was never added or was
// deliberately removed -- same protection injectNavbarIntoPage already
// honors for a single page. Without this, only the brand-new page would get
// a nav reflecting the current (now-larger) page list, and every
// previously-added page would keep shipping whatever nav existed at the
// moment *it* was created -- stale links pointing nowhere, or missing the
// pages added after it. See issue #36.
function syncNavbarAcrossPages(editor: any): void {
  try {
    const navHtml = buildNavHtml(getNavConfig(editor));
    const pages = editor.Pages?.getAll?.() || [];
    pages.forEach((page: any) => {
      if (isEmptyPage(page)) injectNavbarIntoPage(editor, page);
      else if (pageHasNavbar(page)) replaceNavbarInPage(page, navHtml);
    });
  } catch (e) {
    console.warn('🌐 Could not sync navbar across pages:', e);
  }
}

export function registerGlobalNavbar(editor: GrapesJSEditor): void {
  console.log('🌐 Registering Global Navbar plugin...');

  // Wait for onReady before touching Pages/components at all. Per GrapesJS's
  // own event docs, 'canvas:frame:load' (used by an earlier version of this
  // file) fires "right after iframe's onload" — before the canvas's
  // injected scripts (Tailwind CDN, UIkit, sequential, can take 1s+) finish
  // loading and before the frame's body is actually rendered
  // ('canvas:frame:load:body' is the real event for that). onReady is the
  // documented "safe to do anything" signal — the same one @grapesjs/react
  // itself waits on before ever showing the real canvas instead of its own
  // loading placeholder. onReady() runs its callback immediately if the
  // editor is already past that point, so this is safe to call unconditionally.
  (editor as any).onReady(() => {
    // Bake the navbar into whichever page is current right now, but only if
    // it's genuinely empty — this covers the brand-new-org case (GrapesJS's
    // own built-in default page) without touching any real, already-loaded
    // page.
    const currentPage = (editor as any).Pages?.getSelected?.();
    if (currentPage && isEmptyPage(currentPage)) {
      injectNavbarIntoPage(editor, currentPage);
    }

    // Page events live on the editor itself ('page:select', 'page:add'), not
    // on editor.Pages — editor.Pages (the PageManager) doesn't expose .on()
    // directly. Calling editor.Pages.on(...) threw synchronously and was
    // silently aborting the rest of this function's setup on every load.
    // Syncs every page with a navbar (not just the new one) so links stay
    // correct across the whole site as pages are added -- see issue #36.
    (editor as any).on('page:add', () => {
      syncNavbarAcrossPages(editor);
    });

    // Canvas-level style overrides touch the raw iframe DOM directly (not
    // GrapesJS's component/style model), so they only need the frame to
    // exist, not the whole editor to be ready — but there's no harm in
    // running this inside onReady too now that everything else is here.
    (editor as any).on('canvas:frame:load', () => injectNavCanvasStyles(editor));

    console.log('✅ Global Navbar registered');
  });
}

export default registerGlobalNavbar;
